import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { todayLocal } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { MonthRevenueChart, OccupancyMonthChart } from "@/pages/reports/reportCharts";
import type { YearlyReport } from "@/types/camping";
import {
  DeltaBadge,
  LogicNote,
  MONTH_LABELS,
  StatCard,
  downloadCsv,
  formatPercent,
  percentChange,
} from "@/pages/reports/reportShared";

function currentYearValue(): number {
  return Number.parseInt(todayLocal().slice(0, 4), 10);
}

export function YearlyReportSection() {
  const [selectedYear, setSelectedYear] = useState(currentYearValue);
  const [report, setReport] = useState<YearlyReport | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!selectedYear) return;
    setLoading(true);
    setError(null);
    api
      .get<YearlyReport>(`/api/v1/reports/yearly?year=${selectedYear}`)
      .then((data) => {
        setReport(data);
        if (data.available_years?.length) {
          setAvailableYears(data.available_years);
        }
      })
      .catch((err: unknown) => {
        setReport(null);
        setError(err instanceof Error ? err.message : "Jahresauswertung konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [selectedYear]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]!);
    }
  }, [availableYears, selectedYear]);

  const currency = report?.currency ?? "EUR";

  const yearOptions = useMemo(() => {
    const years = availableYears.length > 0 ? availableYears : [currentYearValue()];
    return years.filter((y) => y >= 2000 && y <= 2100);
  }, [availableYears]);

  const revenueChange = useMemo(() => {
    if (!report?.prior_year) return null;
    const current = Number.parseFloat(report.invoiced_total) || 0;
    const prior = Number.parseFloat(report.prior_year.invoiced_total) || 0;
    return percentChange(current, prior);
  }, [report]);

  const priorByMonth = useMemo(() => {
    if (!report?.prior_year) return null;
    return report.prior_year.months;
  }, [report]);

  const exportCsv = () => {
    if (!report) return;
    const rows: string[][] = [
      ["Kennzahl", "Wert"],
      ["Jahr", String(report.year)],
      ["Umsatz gesamt", report.invoiced_total],
      [],
      ["Monat", "Umsatz", "Auslastung %"],
      ...report.months.map((m) => [
        MONTH_LABELS[m.month - 1] ?? String(m.month),
        m.invoiced_total,
        m.occupancy_rate_percent ?? "",
      ]),
    ];
    if (report.prior_year) {
      rows.push(
        [],
        ["Vorjahr", String(report.prior_year.year)],
        ["Vorjahr Umsatz gesamt", report.prior_year.invoiced_total],
        [],
        ["Monat (Vorjahr)", "Umsatz"],
        ...report.prior_year.months.map((m) => [
          MONTH_LABELS[m.month - 1] ?? String(m.month),
          m.invoiced_total,
        ]),
      );
    }
    rows.push(
      [],
      ["Kostenposition (Platzhalter)", "Betrag"],
      ...report.cost_placeholders.map((p) => [p.label, p.amount ?? "—"]),
    );
    downloadCsv(`jahresauswertung_${report.year}.csv`, rows);
  };

  const highlightMonth = selectedYear === currentYearValue()
    ? Number.parseInt(todayLocal().slice(5, 7), 10)
    : null;

  const peakMonth = useMemo(() => {
    if (!report || report.months.length === 0) return null;
    return report.months.reduce((best, month) =>
      (Number.parseFloat(month.invoiced_total) || 0) > (Number.parseFloat(best.invoiced_total) || 0)
        ? month
        : best,
    );
  }, [report]);

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-label">Jahresverlauf</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Umsatz und Auslastung für {selectedYear}
            {report?.prior_year ? `, daneben ${report.prior_year.year}` : ""}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="block text-sm">
            <span className="mb-1 block text-label-secondary">Jahr</span>
            {yearOptions.length > 0 ? (
              <select
                className="eh-input min-h-[44px] w-full sm:w-36"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number.parseInt(e.target.value, 10))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min={2000}
                max={2100}
                className="eh-input min-h-[44px] w-full sm:w-28"
                value={selectedYear}
                onChange={(e) =>
                  setSelectedYear(Number.parseInt(e.target.value, 10) || currentYearValue())
                }
              />
            )}
          </label>
          <button
            type="button"
            className="eh-btn eh-btn--secondary min-h-[44px] whitespace-nowrap"
            disabled={!report}
            onClick={exportCsv}
          >
            CSV exportieren
          </button>
        </div>
      </header>

      {error ? (
        <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Jahresauswertung wird geladen…</p>
      ) : report ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              label="Jahresumsatz (ausgestellte Rechnungen)"
              value={formatCurrency(report.invoiced_total, currency)}
              hint={
                revenueChange && report.prior_year
                  ? `Vorjahr: ${formatCurrency(report.prior_year.invoiced_total, currency)} (${revenueChange})`
                  : "Nach Rechnungsdatum, Summe aller Monate."
              }
            />
            <StatCard
              label="Stärkster Monat"
              value={peakMonth ? (MONTH_LABELS[peakMonth.month - 1] ?? "—") : "—"}
              hint={
                peakMonth
                  ? formatCurrency(peakMonth.invoiced_total, currency)
                  : "Noch kein Monatsumsatz."
              }
            />
          </div>

          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <h3 className="font-medium text-label">Monatsumsätze</h3>
            <div className="mt-4">
              <MonthRevenueChart
                months={report.months}
                priorMonths={priorByMonth}
                currency={currency}
                currentYearLabel={String(report.year)}
                priorYearLabel={report.prior_year ? String(report.prior_year.year) : undefined}
                highlightMonth={highlightMonth}
              />
            </div>
          </section>

          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <h3 className="font-medium text-label">Auslastung</h3>
            <div className="mt-4">
              <OccupancyMonthChart months={report.months} />
            </div>
          </section>

          <section className="overflow-x-auto rounded-xl border border-separator bg-surface shadow-sm">
            <table className="eh-table w-full min-w-[32rem] text-sm">
              <thead>
                <tr>
                  <th className="text-left">Monat</th>
                  <th className="text-right">Umsatz</th>
                  {report.prior_year ? (
                    <th className="text-right">Vorjahr ({report.prior_year.year})</th>
                  ) : null}
                  {report.prior_year ? <th className="text-right">Veränderung</th> : null}
                  <th className="text-right">Auslastung</th>
                </tr>
              </thead>
              <tbody>
                {report.months.map((row, index) => {
                  const prior = report.prior_year?.months[index];
                  const currentAmount = Number.parseFloat(row.invoiced_total) || 0;
                  const priorAmount = prior ? Number.parseFloat(prior.invoiced_total) || 0 : null;
                  return (
                    <tr key={row.month}>
                      <td>{MONTH_LABELS[row.month - 1]}</td>
                      <td className="text-right tabular-nums">
                        {formatCurrency(row.invoiced_total, currency)}
                      </td>
                      {report.prior_year ? (
                        <td className="text-right tabular-nums text-label-secondary">
                          {prior ? formatCurrency(prior.invoiced_total, currency) : "—"}
                        </td>
                      ) : null}
                      {report.prior_year ? (
                        <td className="text-right">
                          {priorAmount != null ? (
                            <span className="inline-flex justify-end">
                              <DeltaBadge current={currentAmount} prior={priorAmount} />
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      ) : null}
                      <td className="text-right tabular-nums text-label-secondary">
                        {formatPercent(row.occupancy_rate_percent)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t border-separator font-medium">
                  <td>Gesamt</td>
                  <td className="text-right tabular-nums">
                    {formatCurrency(report.invoiced_total, currency)}
                  </td>
                  {report.prior_year ? (
                    <td className="text-right tabular-nums text-label-secondary">
                      {formatCurrency(report.prior_year.invoiced_total, currency)}
                    </td>
                  ) : null}
                  {report.prior_year ? (
                    <td className="text-right">
                      <span className="inline-flex justify-end">
                        <DeltaBadge
                          current={Number.parseFloat(report.invoiced_total) || 0}
                          prior={Number.parseFloat(report.prior_year.invoiced_total) || 0}
                        />
                      </span>
                    </td>
                  ) : null}
                  <td />
                </tr>
              </tbody>
            </table>
          </section>

          <details className="rounded-xl border border-separator bg-surface px-4 py-3 text-sm shadow-sm">
            <summary className="cursor-pointer font-medium text-label">
              Kostenpositionen
            </summary>
            <p className="mt-2 text-label-secondary">
              Platzhalter für eine spätere Kostenbuchhaltung. Beträge sind noch nicht erfasst.
            </p>
            <ul className="mt-3 divide-y divide-separator">
              {report.cost_placeholders.map((item) => (
                <li
                  key={item.key}
                  className="flex items-center justify-between gap-4 py-3 text-sm first:pt-0 last:pb-0"
                >
                  <span className="text-label">{item.label}</span>
                  <span className="shrink-0 text-label-tertiary">—</span>
                </li>
              ))}
            </ul>
          </details>

          <LogicNote>
            <p>{report.logic_note}</p>
          </LogicNote>
        </>
      ) : null}
    </section>
  );
}
